import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import supportApiService from '../../services/supportApiService';

const SupportAgentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showDisapproveModal, setShowDisapproveModal] = useState(false);

  useEffect(() => {
    if (id) {
      loadAgentDetails();
    }
  }, [id]);

  const loadAgentDetails = async () => {
    try {
      setLoading(true);
      console.log('🔍 Loading agent details for ID:', id);
      const data = await supportApiService.getAccountManagerDetails(id);
      console.log('📊 Agent Details API Response:', data);
      
      if (data.success) {
        setAgent(data.user);
        console.log('✅ Agent details loaded successfully');
      } else {
        console.error('❌ Agent Details API returned error:', data.message);
        toast.error(data.message || 'Failed to load agent details');
        navigate('/support/dashboard/account-manager');
      }
    } catch (error) {
      console.error('❌ Agent details load error:', error);
      toast.error('Failed to load agent details');
      navigate('/support/dashboard/account-manager');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setActionLoading(true);
      console.log('✅ Approving agent ID:', id);
      const data = await supportApiService.approveAgent(id);
      
      if (data.success) {
        toast.success('Agent approved successfully');
        // Reload agent details to get updated status
        await loadAgentDetails();
        setShowApproveModal(false);
      } else {
        toast.error(data.message || 'Failed to approve agent');
      }
    } catch (error) {
      console.error('❌ Approve agent error:', error);
      toast.error('Failed to approve agent');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisapprove = async () => {
    try {
      setActionLoading(true);
      console.log('❌ Disapproving agent ID:', id);
      const data = await supportApiService.disapproveAgent(id);
      
      if (data.success) {
        toast.success('Agent disapproved successfully');
        // Reload agent details to get updated status
        await loadAgentDetails();
        setShowDisapproveModal(false);
      } else {
        toast.error(data.message || 'Failed to disapprove agent');
      }
    } catch (error) {
      console.error('❌ Disapprove agent error:', error);
      toast.error('Failed to disapprove agent');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (approved) => {
    if (approved === true) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
          <i className="fas fa-check-circle mr-2"></i>
          APPROVED
        </span>
      );
    } else if (approved === false) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
          <i className="fas fa-times-circle mr-2"></i>
          DISAPPROVED
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
          <i className="fas fa-clock mr-2"></i>
          PENDING
        </span>
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-exclamation-triangle text-red-500 text-4xl mb-4"></i>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Agent Not Found</h2>
          <p className="text-gray-600 mb-4">The requested agent details could not be found.</p>
          <button
            onClick={() => navigate('/support/dashboard/account-manager')}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md"
          >
            Back to Agent Management
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Agent Details</h1>
              <p className="text-gray-600">View and manage agent information</p>
            </div>
            <button
              onClick={() => navigate('/support/dashboard/account-manager')}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm"
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Back to Agents
            </button>
          </div>
        </div>

        {/* Agent Details Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Card Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Agent Information</h3>
              {getStatusBadge(agent.approved)}
            </div>
          </div>

          {/* Agent Details Form */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* First Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={agent.firstName || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                />
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={agent.lastName || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={agent.email || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                />
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="text"
                  value={agent.phoneNumber || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                />
              </div>

              {/* City */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  value={agent.city || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                />
              </div>

              {/* State */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State
                </label>
                <input
                  type="text"
                  value={agent.state || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                />
              </div>

              {/* Country */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <input
                  type="text"
                  value={agent.country || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                />
              </div>

              {/* ZIP */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ZIP Code
                </label>
                <input
                  type="text"
                  value={agent.zip || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                />
              </div>

              {/* PAN Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  PAN Number
                </label>
                <input
                  type="text"
                  value={agent.panNumber || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                />
              </div>

              {/* GST Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  GST Number
                </label>
                <input
                  type="text"
                  value={agent.gstNumber || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                />
              </div>

              {/* Udyam Aadhar Registration Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Udyam Aadhar Reg. Number
                </label>
                <input
                  type="text"
                  value={agent.udyamAadharRegNo || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                />
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <input
                  type="text"
                  value={agent.role || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-8 flex justify-center space-x-4">
              {agent.approved === true && (
                <button
                  onClick={() => setShowDisapproveModal(true)}
                  disabled={actionLoading}
                  className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-md font-medium transition-colors disabled:opacity-50"
                >
                  {actionLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </div>
                  ) : (
                    <>
                      <i className="fas fa-times mr-2"></i>
                      Disapprove
                    </>
                  )}
                </button>
              )}

              {agent.approved === false && (
                <button
                  onClick={() => setShowApproveModal(true)}
                  disabled={actionLoading}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-md font-medium transition-colors disabled:opacity-50"
                >
                  {actionLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </div>
                  ) : (
                    <>
                      <i className="fas fa-check mr-2"></i>
                      Approve
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Approve Agent
              </h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to approve this agent? This will grant them access to the system.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowApproveModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApprove}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md disabled:opacity-50"
                >
                  {actionLoading ? 'Processing...' : 'Yes, Approve'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Disapprove Modal */}
      {showDisapproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Disapprove Agent
              </h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to disapprove this agent? This will revoke their system access.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDisapproveModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDisapprove}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md disabled:opacity-50"
                >
                  {actionLoading ? 'Processing...' : 'Yes, Disapprove'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportAgentDetails;